/**
 * PendingStepBannerPure
 *
 * Pure presentational component for pending step banner.
 * No server dependencies - all data and callbacks passed via props.
 */
import { useEffect, useRef, useState } from 'react';

import { cn } from '../cn';
import { Button } from '../Input2';
import { Alerts, Time } from '../StrokeIcons';
import { sanitizeHtml } from '../utils/sanitizeHtml';

// ============================================================================
// Types
// ============================================================================

export type PendingStepBannerPureProps = {
  /** Stable identity of the pending step. When this changes, the banner briefly flashes
      to draw the user's eye to "this is the queued send." Pass the ai_step / step ID. */
  stepId?: string | null;
  bodyPreview: string | null;
  type: string;
  scheduledFor: string | null;
  /** ai_step.status — used to distinguish "approved & scheduled to send" from
      "drafted, waiting for the user to review." Only 'approved' means the user has
      authorized the send; everything else (not_started, researched, waiting_for_user)
      still needs their review. */
  status?: string | null;
  onDelete: () => void;
  isDeleting?: boolean;
  /** Send this step right away, skipping the LinkedIn schedule window once. Only offered for
      `approved` steps — a drafted step still needs review. Absent → no button. */
  onSendNow?: () => void;
  isSendingNow?: boolean;
};

// ============================================================================
// Helper Functions
// ============================================================================

function formatScheduledTime(scheduledFor: string | null): string {
  if (!scheduledFor) return 'soon';

  const date = new Date(scheduledFor);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMs < 0) return 'soon';
  if (diffMins < 60) return `in ${diffMins} minute${diffMins !== 1 ? 's' : ''}`;
  // Within 24h: include the actual clock time so the user can see WHEN, not just "in 4 hours."
  if (diffHours < 24) {
    const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `in ${diffHours} hour${diffHours !== 1 ? 's' : ''} (${timeStr})`;
  }

  // Beyond 24h: show the day + time so it's unambiguous.
  const dateStr = date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${dateStr} at ${timeStr}`;
}

function getStepTypeLabel(type: string): string {
  switch (type) {
    case 'message':
      return 'message';
    case 'inmail':
      return 'InMail';
    case 'connect':
      return 'connection request';
    case 'video_note':
      return 'video note';
    case 'audio':
    case 'voice_note':
      return 'voice note';
    case 'email':
      return 'email';
    default:
      return type;
  }
}

// ============================================================================
// Component
// ============================================================================

export function PendingStepBannerPure({
  stepId,
  bodyPreview,
  type,
  scheduledFor,
  status,
  onDelete,
  isDeleting,
  onSendNow,
  isSendingNow,
}: PendingStepBannerPureProps) {
  const scheduledTimeLabel = formatScheduledTime(scheduledFor);
  const stepTypeLabel = getStepTypeLabel(type);
  const needsReview = status != null && status !== 'approved';

  // Flash the banner + scroll into view when a new step appears, so the user sees
  // confirmation that their generate-and-approve flow actually queued a send. Without
  // this, users wonder whether the action took effect and re-trigger generation.
  const ref = useRef<HTMLDivElement>(null);
  const [flashing, setFlashing] = useState(false);
  const prevStepId = useRef<string | null | undefined>(stepId);

  useEffect(() => {
    if (stepId == null) {
      prevStepId.current = stepId;
      return;
    }
    if (prevStepId.current === stepId) return;
    prevStepId.current = stepId;
    setFlashing(true);
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const t = setTimeout(() => setFlashing(false), 1500);
    return () => clearTimeout(t);
  }, [stepId]);

  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 transition-shadow dark:border-amber-800 dark:bg-amber-900/20',
        flashing && 'shadow-[0_0_0_3px_rgba(245,158,11,0.45)] dark:shadow-[0_0_0_3px_rgba(217,119,6,0.45)]',
      )}>
      <div className="flex items-start gap-2">
        <Alerts.AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {/* While a send-now is in flight the scheduled time is meaningless — it is going out
                right now — and repeating it reads as "nothing happened". */}
            {isSendingNow
              ? `Sending ${stepTypeLabel} now…`
              : needsReview
                ? `Drafted ${stepTypeLabel} — review to send`
                : `Scheduled ${stepTypeLabel} ${scheduledTimeLabel}`}
          </div>
          {bodyPreview && (
            <div
              className="mt-1 line-clamp-2 text-sm text-amber-700 [&_p]:inline dark:text-amber-300"
              dangerouslySetInnerHTML={{ __html: `"${sanitizeHtml(bodyPreview)}"` }}
            />
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Time.Clock className="size-3.5 text-amber-600 dark:text-amber-400" />
        <span className="text-xs text-amber-700 dark:text-amber-300">
          {isSendingNow
            ? 'Waiting for LinkedIn to confirm delivery'
            : needsReview
              ? 'Generating a new draft will replace this one'
              : 'Scheduling a new message will replace this one'}
        </span>
        <div className="flex-1" />
        {onSendNow != null && !needsReview && !isSendingNow && (
          <Button
            size="xs"
            color="green"
            onClick={onSendNow}
            disabled={isSendingNow || isDeleting}
            data-testid="pending-step-send-now"
            tooltip="Send right away instead of waiting for your LinkedIn hours">
            {isSendingNow ? 'Sending…' : 'Send now'}
          </Button>
        )}
        {!isSendingNow && (
          <Button size="xs" color="red" tone="soft" onClick={onDelete} disabled={isDeleting}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}
